import { UserRoles } from "@/core";
import { useUser } from "@clerk/clerk-react";
import { useEffect } from "react";
import { create } from "zustand";

interface ClerkUser {
  id: string;
  emailAddresses: Array<{
    emailAddress: string;
    id: string;
  }>;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string;
  username: string | null;
  createdAt: Date;
  updatedAt: Date;
  publicMetadata: Record<string, unknown>;
  roles: UserRoles[];
}

interface UserStore {
  user: ClerkUser | null;
  isLoading: boolean;
  error: string | null;
  setUser: (user: ClerkUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  getUserEmail: () => string | null;
  getUserFullName: () => string;
  getUserInitials: () => string;
  hasFullAccess: () => boolean;
  hasClientVendorWriteAccess: () => boolean;
  isAdmin: () => boolean;
}

export const useUserStore = create<UserStore>((set, get) => ({
  user: null,
  isLoading: true,
  error: null,
  setUser: (user) => set({ user, error: null }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error, isLoading: false }),
  getUserEmail: () => {
    const { user } = get();
    return user?.emailAddresses[0]?.emailAddress || null;
  },
  getUserFullName: () => {
    const { user } = get();
    if (!user) return "";
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || user.username || "User";
  },
  getUserInitials: () => {
    const { user } = get();
    if (!user) return "";
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (user.username) {
      return user.username[0].toUpperCase();
    }
    return "U";
  },
  hasFullAccess: () => {
    const { user } = get();
    return user?.roles?.includes(UserRoles.FULL_ACCESS) ?? false;
  },
  hasClientVendorWriteAccess: () => {
    const { user } = get();
    return (
      user?.roles?.some((role) =>
        [UserRoles.FULL_ACCESS, UserRoles.CS_AGENT].includes(role),
      ) ?? false
    );
  },
  isAdmin: () => {
    const { user } = get();
    return (
      user?.roles?.some((role) =>
        [UserRoles.FULL_ACCESS, UserRoles.ADMIN].includes(role),
      ) ?? false
    );
  },
}));

// Hook to initialize user data from Clerk
export const useInitializeUser = () => {
  const { user, isLoaded, isSignedIn } = useUser();
  const { setUser, setLoading, setError } = useUserStore();

  useEffect(() => {
    setLoading(!isLoaded);

    if (isLoaded) {
      if (isSignedIn && user) {
        setUser({
          id: user.id,
          emailAddresses: user.emailAddresses,
          firstName: user.firstName,
          lastName: user.lastName,
          imageUrl: user.imageUrl,
          username: user.username,
          createdAt: user.createdAt!,
          updatedAt: user.updatedAt!,
          publicMetadata: user.publicMetadata || {},
          roles: Array.isArray(user.publicMetadata.roles)
            ? user.publicMetadata.roles
            : [],
        });
      } else {
        setUser(null);
      }
      setError(null);
    }
  }, [user, isLoaded, isSignedIn, setUser, setLoading, setError]);

  return { isLoading: !isLoaded, error: null };
};
